import { createUseStyles, withTheme } from '@glu/theming';
import React, { useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import locale from '@glu/locale';
import { CaretLeftIcon, CaretRightIcon } from '@glu/icons-react';
import moment from 'moment';
import { AccessibilityText } from '@glu/utilities-react';
import styles from './MonthNav.styles';
import SelectMonths from './SelectMonths';
import SelectYears from './SelectYears';
import { useDropdownContext } from './DropdownContext';

const useStyles = createUseStyles(styles);

const MonthNav = ({
  dateMax,
  dateMin,
  first,
  firstFocusedElement,
  head,
  htmlId,
  index,
  last,
  monthDropdown,
  setHead,
  yearDropdown,
  yearDropdownMaxYear,
  yearDropdownMinYear,
  ...rest
}) => {
  const classes = useStyles(rest);
  const { focusDate, focusOn } = useDropdownContext();

  const prevMonth = moment(head).add(-1, 'months');
  const nextMonth = moment(head).add(1, 'months');
  const handlePrevClick = () => {
    setHead(prevMonth);
  };
  const handleNextClick = () => {
    setHead(nextMonth);
  };

  const handleSelectMonth = (month) => {
    setHead(moment(head).month(month));
  };
  const handleSelectYear = (year) => {
    setHead(moment(head).year(year));
  };
  const previousButtonRef = useRef();
  const nextButtonRef = useRef();

  /* istanbul ignore next */
  useEffect(() => {
    if (firstFocusedElement) {
      previousButtonRef.current?.focus();
    }
  }, [firstFocusedElement]);

  useEffect(() => {
    if (focusOn === 'nav') {
      if (first) {
        previousButtonRef.current?.focus();
      } else if (last) {
        nextButtonRef.current?.focus();
      }
    }
  }, [focusOn, first, last]);

  useEffect(() => {
    if (focusDate) {
      const focusDateMonth = focusDate.month();
      const focusDateYear = focusDate.year();
      const currMonth = head.month();
      const currYear = head.year();
      if (
        ((focusDateMonth === 11 && currMonth === 0) || focusDateMonth < currMonth) &&
        currYear >= focusDateYear &&
        first
      ) {
        handlePrevClick();
      } else if (
        ((focusDateMonth === 0 && currMonth === 11) || focusDateMonth > currMonth) &&
        currYear <= focusDateYear &&
        last
      ) {
        handleNextClick();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusDate]);

  return (
    <div className={classes.header}>
      {first && (
        <button
          ref={previousButtonRef}
          aria-label={locale.get('prevMonth')}
          className={`${classes.button} ${classes.prev} ${first && classes.first}`}
          data-qa="previous-button"
          onClick={handlePrevClick}
          type="button"
        >
          <AccessibilityText>{locale.get('prevMonth')}</AccessibilityText>
          <CaretLeftIcon />
        </button>
      )}
      <div className={classes.title} id={`${htmlId}-${index}-calendarHead`}>
        <div className={classes.select}>
          {monthDropdown ? (
            <SelectMonths head={head} index={index} onChange={handleSelectMonth} />
          ) : (
            locale.get(`month${head.month() + 1}`)
          )}
        </div>

        <div className={classes.select}>
          {yearDropdown ? (
            <SelectYears
              head={head}
              index={index}
              onChange={handleSelectYear}
              yearDropdownMaxYear={yearDropdownMaxYear}
              yearDropdownMinYear={yearDropdownMinYear}
            />
          ) : (
            head.format('YYYY')
          )}
        </div>
      </div>
      {last && (
        <button
          ref={nextButtonRef}
          aria-label={locale.get('nextMonth')}
          className={`${classes.button} ${classes.next} ${last && classes.last}`}
          data-qa="next-button"
          onClick={handleNextClick}
          type="button"
        >
          <AccessibilityText>{locale.get('nextMonth')}</AccessibilityText>
          <CaretRightIcon />
        </button>
      )}
    </div>
  );
};

MonthNav.propTypes = {
  dateMax: PropTypes.string.isRequired,
  dateMin: PropTypes.string.isRequired,
  first: PropTypes.bool.isRequired,
  firstFocusedElement: PropTypes.bool.isRequired,
  head: PropTypes.shape({
    format: PropTypes.func,
    month: PropTypes.func,
    year: PropTypes.func,
  }).isRequired,
  htmlId: PropTypes.string.isRequired,
  index: PropTypes.number.isRequired,
  last: PropTypes.bool.isRequired,
  monthDropdown: PropTypes.bool.isRequired,
  setHead: PropTypes.func.isRequired,
  yearDropdown: PropTypes.bool.isRequired,
  yearDropdownMaxYear: PropTypes.number.isRequired,
  yearDropdownMinYear: PropTypes.number.isRequired,
};

export default withTheme(MonthNav);
